// Filmstrip (Alpine.js component): drag-to-pan & ticker
// See `templates/_components/filmstrip.twig`

document.addEventListener('alpine:init', () => {
  window.Alpine.data(
    'filmstrip',
    ({ ticker = false, speed = 30, resumeDelay = 2000 } = {}) => ({
      isTicker: false,
      // A user's pause (via the toggle) holds across page loads & filmstrips
      isPaused: window.Alpine.$persist(false).as('filmstrip:paused'),
      dragOrigin: null,
      hasMoved: false,
      idleUntil: 0,
      position: 0,
      loopWidth: 0,
      loopStart: 0,
      originalEls: [],
      frameId: null,
      isOnScreen: false,
      onVisibility: null,

      init() {
        if (
          !ticker ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
          return;
        }
        this.isTicker = true;
        // Pause while the tab is in the background
        this.onVisibility = () =>
          document.hidden ? this.stop() : this.start();
        document.addEventListener('visibilitychange', this.onVisibility);
      },

      destroy() {
        this.stop();
        document.removeEventListener('visibilitychange', this.onVisibility);
      },

      // Runs once the track's ref exists (after `init()`, see `track`)
      initTrack() {
        if (!this.isTicker) {
          return;
        }
        this.originalEls = [...this.$refs.trackEl.children];
        // Also fires once on observe, for the initial measure
        new ResizeObserver(() => this.measure()).observe(this.$refs.trackEl);
      },

      // Clones

      appendCopy() {
        this.originalEls.forEach((itemEl, index) => {
          const cloneEl = itemEl.cloneNode(true);
          cloneEl.setAttribute('aria-hidden', 'true');
          cloneEl.dataset.filmstripClone = index;
          // Keep clones out of the tab order & PhotoSwipe (clicks are forwarded)
          cloneEl.querySelectorAll('a').forEach((linkEl) => {
            linkEl.tabIndex = -1;
            linkEl.classList.remove('photoswipe');
          });
          // Let lazysizes pick up clones of images still mid-load
          cloneEl.querySelectorAll('.lazyloading').forEach((el) => {
            el.classList.replace('lazyloading', 'lazyload');
          });
          this.$refs.trackEl.append(cloneEl);
        });
      },

      measure() {
        const trackEl = this.$refs.trackEl;
        if (trackEl.children.length === this.originalEls.length) {
          this.appendCopy();
        }
        // Sub-pixel precise (unlike offsetLeft), for a seamless wrap
        const left = (el) => el.getBoundingClientRect().left;
        this.loopWidth =
          left(trackEl.children[this.originalEls.length]) -
          left(this.originalEls[0]);
        // The track bleeds into the gutter (padding), which is empty at
        // scrollLeft 0. Loop past it, so items scroll fully out of view.
        this.loopStart = parseFloat(getComputedStyle(trackEl).paddingLeft) || 0;
        // Enough copies to scroll a full loop without hitting the end
        while (
          trackEl.scrollWidth - trackEl.clientWidth <=
          this.loopStart + this.loopWidth
        ) {
          this.appendCopy();
        }
        this.scrollTo(this.position);
      },

      // Scrolling

      scrollTo(x) {
        // Wrap within one loop (ticker only), so it's endless both ways
        const loop = this.loopWidth;
        const start = this.loopStart;
        this.position = loop
          ? ((((x - start) % loop) + loop) % loop) + start
          : x;
        this.$refs.trackEl.scrollLeft = this.position;
      },

      onScroll() {
        const left = this.$refs.trackEl.scrollLeft;
        if (Math.abs(left - this.position) <= 1) {
          return; // our own (programmatic) scroll
        }
        this.nudge();
        if (
          this.loopWidth &&
          (left < this.loopStart || left >= this.loopStart + this.loopWidth)
        ) {
          this.scrollTo(left);
        } else {
          this.position = left;
        }
      },

      // Ticker

      nudge() {
        this.idleUntil = performance.now() + resumeDelay;
      },

      // Runs only while on screen (see `x-intersect`) and the tab is visible

      enter() {
        this.isOnScreen = true;
        this.start();
      },

      leave() {
        this.isOnScreen = false;
        this.stop();
      },

      start() {
        if (
          !this.isTicker ||
          !this.isOnScreen ||
          document.hidden ||
          this.frameId
        ) {
          return;
        }
        const trackEl = this.$refs.trackEl;
        let last = null;
        const tick = (now) => {
          this.frameId = requestAnimationFrame(tick);
          // Cap the step, in case of a long frame
          const dt = last ? Math.min(now - last, 100) : 0;
          last = now;
          if (
            !this.isPaused &&
            !this.dragOrigin &&
            now >= this.idleUntil &&
            // Only keyboard focus pauses; a mouse click on an image shouldn't
            !trackEl.matches(':hover, :focus-visible, :has(:focus-visible)')
          ) {
            this.scrollTo(this.position + (speed * dt) / 1000);
          }
        };
        this.frameId = requestAnimationFrame(tick);
      },

      stop() {
        cancelAnimationFrame(this.frameId);
        this.frameId = null;
      },

      // Drag to pan (mouse); any pointer press counts as interaction

      get isDragging() {
        return Boolean(this.dragOrigin && this.hasMoved);
      },

      dragStart(e) {
        this.nudge();
        if (e.pointerType !== 'mouse' || e.button !== 0) {
          return;
        }
        this.dragOrigin = { x: e.clientX, left: this.position };
        this.hasMoved = false;
      },

      dragMove(e) {
        if (!this.dragOrigin) {
          return;
        }
        const dx = e.clientX - this.dragOrigin.x;
        if (!this.hasMoved && Math.abs(dx) < 5) {
          return;
        }
        if (!this.hasMoved) {
          this.hasMoved = true;
          this.$refs.trackEl.setPointerCapture(e.pointerId);
        }
        this.scrollTo(this.dragOrigin.left - dx);
      },

      dragEnd() {
        this.dragOrigin = null;
        this.nudge();
      },

      onClick(e) {
        // Swallow the click that ends a drag
        if (this.hasMoved) {
          e.preventDefault();
          e.stopPropagation();
          this.hasMoved = false;
          return;
        }
        // Forward clicks on clones to their original (opens PhotoSwipe)
        const cloneEl = e.target.closest('[data-filmstrip-clone]');
        const originalLinkEl = cloneEl
          ? this.originalEls[cloneEl.dataset.filmstripClone].querySelector('a')
          : null;
        if (originalLinkEl) {
          e.preventDefault();
          e.stopPropagation();
          originalLinkEl.click();
        }
      },

      // Bindings (applied in the template with `x-bind`)

      track: {
        'x-ref': 'trackEl',
        'x-init'() {
          this.initTrack();
        },
        'x-intersect:enter'() {
          this.enter();
        },
        'x-intersect:leave'() {
          this.leave();
        },
        '@scroll.passive'() {
          this.onScroll();
        },
        '@pointerdown'(e) {
          this.dragStart(e);
        },
        '@pointermove'(e) {
          this.dragMove(e);
        },
        '@pointerup'() {
          this.dragEnd();
        },
        '@pointercancel'() {
          this.dragEnd();
        },
        '@dragstart.prevent'() {},
        '@click.capture'(e) {
          this.onClick(e);
        },
      },

      // Pause/play toggle: only rendered by Alpine, and only for a ticker
      toggle: {
        'x-if'() {
          return this.isTicker;
        },
      },

      toggleButton: {
        '@click'() {
          this.isPaused = !this.isPaused;
          this.idleUntil = 0;
        },
      },
    }),
  );
});
